import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import api from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Inserisci la tua email");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Il backend risponde sempre 200 per sicurezza, ma se qualcosa va storto mostralo
      toast.error(err?.response?.data?.detail || "Errore durante l'invio. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "radial-gradient(ellipse at top, #143830 0%, #0A1F18 100%)" }}>
      <div className="w-full max-w-md">
        <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono text-sheriff-gold/70 mb-2">
              <ShieldAlert className="h-3 w-3" /> Recupero credenziali
            </div>
            <h1 className="font-heading font-black text-3xl text-[#F5E6B8] tracking-tight" data-testid="forgot-password-title">
              Password Dimenticata
            </h1>
            <p className="text-sm text-[#B8A269] mt-2">
              Inserisci l&apos;email del tuo account. Ti invieremo un link per reimpostare la password.
            </p>
          </div>

          {sent ? (
            <div className="text-center py-6 space-y-4" data-testid="forgot-password-sent">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/15 border border-emerald-500/40">
                <CheckCircle2 className="h-7 w-7 text-emerald-400" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg text-[#F5E6B8] mb-2">Controlla la tua email</div>
                <p className="text-sm text-[#B8A269] leading-relaxed">
                  Se <span className="text-sheriff-gold font-semibold">{email}</span> corrisponde
                  a un account registrato, riceverai un link di reset entro pochi minuti.
                </p>
                <p className="text-xs text-sheriff-gold/50 mt-3">
                  Il link scadrà tra <strong>30 minuti</strong>. Controlla anche la cartella spam.
                </p>
              </div>
              <Link to="/login" className="inline-flex items-center gap-1 text-sheriff-gold hover:text-[#F5E6B8] text-sm" data-testid="forgot-back-to-login">
                <ArrowLeft className="h-3 w-3" /> Torna al login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label className="text-xs font-semibold text-[#E8D89A] mb-1.5 block">Email registrata</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sheriff-gold/50" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tuoindirizzo@example.com"
                    required
                    autoFocus
                    className="pl-10 bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold"
                data-testid="forgot-password-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                Invia link di reset
              </Button>
              <div className="text-center pt-2">
                <Link to="/login" className="text-xs text-sheriff-gold/70 hover:text-sheriff-gold inline-flex items-center gap-1" data-testid="forgot-back-link">
                  <ArrowLeft className="h-3 w-3" /> Torna al login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
